const AUTH_TOKEN_KEY = "reportforge.token";

export function getToken(): string | null {
  return localStorage.getItem(AUTH_TOKEN_KEY);
}

export function setToken(token: string | null): void {
  if (token) localStorage.setItem(AUTH_TOKEN_KEY, token);
  else localStorage.removeItem(AUTH_TOKEN_KEY);
}

export class ApiError extends Error {
  status: number;
  body: unknown;
  /** True for a raw Spring-Security-level 401/403 (missing/invalid/expired token) rather than
   *  one of our own structured business-logic errors - see apiFetch for how this is decided. */
  isSessionError: boolean;
  constructor(status: number, message: string, body: unknown, isSessionError = false) {
    super(message);
    this.status = status;
    this.body = body;
    this.isSessionError = isSessionError;
  }
}

interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  apiKey?: string;
  skipAuth?: boolean;
  raw?: boolean; // return Response instead of parsed JSON (for binary downloads)
}

export async function apiFetch<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };

  if (opts.apiKey) {
    headers["X-Api-Key"] = opts.apiKey;
  } else if (!opts.skipAuth) {
    const token = getToken();
    if (token) headers["Authorization"] = `Bearer ${token}`;
  }

  const res = await fetch(`/api${path}`, {
    method: opts.method ?? "GET",
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });

  if (opts.raw) {
    if (!res.ok) {
      const text = await res.text().catch(() => res.statusText);
      throw new ApiError(res.status, text, null);
    }
    return res as unknown as T;
  }

  const contentType = res.headers.get("content-type") ?? "";
  const isJson = contentType.includes("application/json");
  const payload = isJson ? await res.json().catch(() => null) : await res.text();

  if (!res.ok) {
    // Two shapes reach here: our own GlobalExceptionHandler ({code, message, timestamp}, thrown
    // from inside a controller) and Spring Security's raw filter-level rejection ({timestamp,
    // status, error, path} - no "message" field, produced for a missing/invalid/expired token
    // before a request ever reaches a controller). res.statusText is frequently just "" over
    // HTTP/2 (which this site negotiates), so falling back to it silently produced an empty
    // error string - which every caller's `if (error)` check then skipped as falsy, rendering a
    // blank page instead of an error. isSessionError below is what tells the caller (see
    // AuthContext/apiFetch callers) this was a stale-session rejection, not a real business error.
    const body = isJson && payload && typeof payload === "object" ? (payload as Record<string, unknown>) : null;
    const message =
      (body?.message as string) || (body?.error as string) || res.statusText || `Request failed (HTTP ${res.status})`;
    // 402 = an on-premise install whose license is missing/expired (see OnPremGuardFilter). The
    // app listens for this to switch to the license screen instead of showing a raw error.
    if (res.status === 402 && body && typeof body.code === "string" && body.code.startsWith("LICENSE_")) {
      window.dispatchEvent(new Event("reportforge:license-blocked"));
    }
    const isSessionError = (res.status === 401 || res.status === 403) && !(body && "code" in body);
    if (isSessionError) {
      // The stored token is definitely no good (expired/invalid/missing) - clear it and let
      // AuthContext (listening for this event) log the user out and send them back to /login,
      // instead of every page individually having to notice and handle a blank/broken render.
      setToken(null);
      window.dispatchEvent(new Event("reportforge:session-expired"));
    }
    throw new ApiError(res.status, message, payload, isSessionError);
  }

  return payload as T;
}

export async function apiDownload(
  path: string,
  body: unknown,
  apiKey?: string
): Promise<{ blob: Blob; filename: string }> {
  const res = await apiFetch<Response>(path, { method: "POST", body, apiKey, raw: true });
  const disposition = res.headers.get("content-disposition") ?? "";
  const match = disposition.match(/filename="?([^"]+)"?/);
  const filename = match ? match[1] : "report";
  const blob = await res.blob();
  return { blob, filename };
}
